import React, { useEffect, useState } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Header } from '@/components/Header';
import { LinktreeHub } from '@/components/LinktreeHub';
import { MarketResearchPromo } from '@/components/MarketResearchPromo';
import { Seo } from '@/components/Seo';
import TakeTourButton from '@/components/TakeTourButton';
import GuestSignInPrompt from '@/components/GuestSignInPrompt';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LandingPage is the public-facing entry point at "/".
 *
 * Now intentionally minimal: a Linktree-style hub that gives every visitor
 * (anonymous, free, VIP) a single screen of canonical destinations. Heavier
 * content discovery (phrase categories, recent blog posts, dialect map,
 * featured "phrase of the day") lives on `/library` (HomePage) where users
 * who tap "Browse the phrase library" land.
 *
 * While the market-research survey promo is active, og:image is swapped
 * to the 1200×630 promo card so social shares feature it instead of the
 * default logo.
 */
const LandingPage = () => {
  const [promo, setPromo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/promo/market-research`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled && data?.enabled) setPromo(data); })
      .catch(() => { /* silent — falls back to default OG image */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-dvh bg-background pb-20 md:pb-4" data-testid="landing-page">
      <Seo
        path="/"
        title="ClearCommunicationApp — Bridging Communication Gaps"
        description="Phrases, translations, and tools for neurodivergent individuals, language learners, and anyone navigating cross-generational or professional conversations."
        image={promo?.og_image_path}
        imageWidth={promo?.og_image_width}
        imageHeight={promo?.og_image_height}
      />
      {/* Sitewide WebSite + Organization JSON-LD for Knowledge Graph + sitelinks search box */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://clearcommunicationapp.com/#organization",
                name: "ClearCommunicationApp",
                url: "https://clearcommunicationapp.com/",
                logo: {
                  "@type": "ImageObject",
                  url: "https://clearcommunicationapp.com/icon-pixel-512.png",
                  width: 512,
                  height: 512,
                },
                sameAs: [
                  "https://www.facebook.com/clearcommunicationapp",
                  "https://www.instagram.com/clearcommunicationapp",
                ],
              },
              {
                "@type": "WebSite",
                "@id": "https://clearcommunicationapp.com/#website",
                url: "https://clearcommunicationapp.com/",
                name: "ClearCommunicationApp",
                description:
                  "Phrases, translations, and tools for neurodivergent individuals, language learners, and anyone navigating cross-generational or professional conversations.",
                publisher: { "@id": "https://clearcommunicationapp.com/#organization" },
                potentialAction: {
                  "@type": "SearchAction",
                  target: {
                    "@type": "EntryPoint",
                    urlTemplate:
                      "https://clearcommunicationapp.com/library?search={search_term_string}",
                  },
                  "query-input": "required name=search_term_string",
                },
              },
            ],
          })}
        </script>
      </Helmet>
      <Header />
      <MarketResearchPromo />
      <LinktreeHub />
      {/* Take-a-tour CTA — fixed at upper-LEFT of the viewport so it
          sits OUTSIDE the centered linktree column and scrolls with
          the page anchored to that corner. Per user feedback May 30,
          2026 the previous floating bottom-left position was wrong. */}
      <TakeTourButton />
      {/* One-time-per-session guest sign-in prompt — only renders if
          the visitor isn't authenticated and hasn't seen the prompt yet
          in this browser session. */}
      <GuestSignInPrompt />
      {/* BottomNav intentionally hidden on the linktree home page so
          the experience stays focused and uncluttered. It still shows
          on every authenticated content page (Library, Blog, Profile, etc). */}
    </div>
  );
};

export default LandingPage;
